import { useTranslation } from 'next-i18next'
import { makeStyles, Container } from '@material-ui/core'
import T from './tokens'
import Reveal from './Reveal'

// Exported so other pages (e.g. /business) can reuse the EXACT same logo set
// as the /new homepage — guaranteeing the images stay identical. It's also the
// default for the `items` prop below, so `<ClientsNew />` on /new is unchanged.
export const clients = [
  { src: '/static/img/home-new/clients-v2/karmart.png', alt: 'KARMART', w: 600, h: 182 },
  { src: '/static/img/home-new/clients-v2/ketshop.png', alt: 'KETSHOPweb', w: 600, h: 111 },
  { src: '/static/img/home-new/clients-v2/lnwshop.png', alt: 'LnwShop', w: 256, h: 40 },
  { src: '/static/img/home-new/clients-v2/zwiz.png', alt: 'ZWIZ.AI', w: 600, h: 300 },
  { src: '/static/img/home-new/clients-v2/shipnergy.png', alt: 'Shipnergy', w: 400, h: 400 },
  { src: '/static/img/home-new/clients-v2/modernair.png', alt: 'Modern Air', w: 276, h: 122 },
  { src: '/static/img/home-new/clients-v2/lapis.png', alt: 'Lapis Insurance Broker', w: 600, h: 380 },
  { src: '/static/img/home-new/clients-v2/giffarine.png', alt: 'Giffarine', w: 250, h: 100 },
  { src: '/static/img/home-new/clients-v2/mycloud.png', alt: 'MyCloud Fulfillment', w: 208, h: 192 },
  { src: '/static/img/home-new/clients-v2/eventpop.png', alt: 'Eventpop', w: 600, h: 200 },
  { src: '/static/img/home-new/clients-v2/tqm.png', alt: 'TQM Life Insurance Broker', w: 600, h: 320 },
  { src: '/static/img/home-new/clients-v2/smoothe.png', alt: 'Smooth E', w: 382, h: 168 },
  { src: '/static/img/home-new/clients-v2/seed.png', alt: 'SE-ED Book Center', w: 376, h: 128 },
  { src: '/static/img/home-new/clients-v2/zort.png', alt: 'Zort', w: 262, h: 114 },
  { src: '/static/img/home-new/clients-v2/shipnity.png', alt: 'Shipnity', w: 511, h: 98 },
  { src: '/static/img/home-new/clients-v2/page365.png', alt: 'Page365', w: 366, h: 160 },
  { src: '/static/img/home-new/clients-v2/popteen.png', alt: 'Pop Teen', w: 600, h: 126 },
  { src: '/static/img/home-new/clients-v2/lotus.png', alt: 'Lotus', w: 571, h: 573 },
]

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '70px 0',
    background: ({ background }) => background,
    [theme.breakpoints.down('sm')]: {
      padding: '40px 0',
    },
  },
  heading: {
    fontFamily: T.font.heading,
    fontSize: 28,
    fontWeight: 500,
    color: T.color.ink,
    textAlign: 'center',
    margin: '0 0 8px',
    // Brand-color any <span> accent inside the heading. The default /new
    // title is a plain string with no <span>, so this rule is inert there
    // (render stays byte-identical); callers passing a title node with a
    // <span> (e.g. /business "SHIPPOP BUSINESS") get the accent for free.
    '& span': {
      color: T.color.brand,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 20,
    },
  },
  subtitle: {
    fontFamily: T.font.body,
    fontSize: 20,
    fontWeight: 500,
    color: T.color.ink,
    textAlign: 'center',
    margin: '0 0 40px',
    '& span': {
      color: T.color.brand,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 16,
      margin: '0 0 24px',
    },
  },
  logoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    justifyContent: 'center',
    alignItems: 'stretch',
    gap: '16px',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(3, 1fr)',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '10px',
    },
  },
  logoItem: {
    background: T.color.surface,
    border: `1px solid ${T.color.line}`,
    borderRadius: T.radius.pill,
    height: 90,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 20px',
    boxShadow: T.shadow.sm,
    transition: 'transform 0.25s ease, box-shadow 0.25s ease',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: T.shadow.md,
    },
    // Every logo shares the SAME bounding box (maxWidth + maxHeight) with
    // object-fit:contain, so each logo is capped by whichever dimension hits
    // first — wide logos by width, square/tall logos by height — giving every
    // mark the same area envelope and equal visual weight. The img width/height
    // ATTRIBUTES are kept as-is (CLS/aspect-ratio hints for `unsized-images`);
    // this CSS overrides them for layout only and never distorts the aspect.
    '& img': {
      maxHeight: 40,
      maxWidth: 120,
      width: 'auto',
      height: 'auto',
      objectFit: 'contain',
    },
    [theme.breakpoints.down('sm')]: {
      height: 68,
      padding: '8px 12px',
      '& img': {
        maxHeight: 30,
        maxWidth: 90,
        width: 'auto',
        height: 'auto',
      },
    },
  },
}))

// Shared "trusted clients" grid. When rendered with no title/subtitle props
// (the /new homepage), it falls back to the translated default strings, so
// `<ClientsNew />` renders the localized homepage content. Other pages (e.g.
// /business) pass their own title, subtitle, logo items and background to
// reuse the same clean 6-column grid — those explicit props take precedence.
const ClientsNew = ({
  title,
  subtitle,
  items = clients,
  background = T.color.surface,
} = {}) => {
  const classes = useStyles({ background })
  const { t } = useTranslation('home')

  const resolvedTitle = title !== undefined ? title : t('clients.title')
  const resolvedSubtitle =
    subtitle !== undefined ? (
      subtitle
    ) : (
      <>
        {t('clients.subtitleBefore')} <span>SHIPPOP</span>
      </>
    )

  return (
    <section className={classes.root}>
      <Container maxWidth={false} style={{ maxWidth: T.container }}>
        <Reveal>
          <h2 className={classes.heading}>{resolvedTitle}</h2>
          <p className={classes.subtitle}>{resolvedSubtitle}</p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className={classes.logoGrid}>
            {items.map((c) => (
              <div key={c.alt} className={classes.logoItem}>
                <img src={c.src} alt={c.alt} width={c.w} height={c.h} loading="lazy" decoding="async" />
              </div>
            ))}
          </div>
        </Reveal>
      </Container>
    </section>
  )
}

export default ClientsNew
